import { useEffect, useState } from "react";

import { ProjectIssuanceTransactionConstants } from "../../../../../../constants";
import { ProjectIssuanceTransactionPanelData } from "../../../../../../models";
import { createIssuanceTransaction } from "../../../../../../service/project";
import { searchIssuanceTransactions, SearchIssuanceTransactionsResponse } from "../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../service/Shared";
import { dataGridMapFilterCriteria, Toast } from "../../../../../../widget";

interface UseReleaseToInventoryModalProps {
  onClose: () => void;
  handleRefresh: () => void;
  parentIssuanceTransaction: SearchIssuanceTransactionsResponse["results"][0];
  issuanceTransactionPanelData: ProjectIssuanceTransactionPanelData;
}

interface UseReleaseToInventoryModalReturnData {
  handleReleaseToInventory: () => void;
  handleCancel: () => void;
  errors?: ServiceError[];
}
export const useReleaseToInventoryModal = ({
  onClose,
  handleRefresh,
  parentIssuanceTransaction,
  issuanceTransactionPanelData,
}: UseReleaseToInventoryModalProps): UseReleaseToInventoryModalReturnData => {
  const [errors, setErrors] = useState<ServiceError[] | undefined>();

  const [relatedProjectIssuanceTransactions, setRelatedProjectIssuanceTransactions] =
    useState<SearchIssuanceTransactionsResponse["results"]>();
  const handleCancel = (): void => {
    handleRefresh();
    onClose();
  };
  const handleReleaseToInventory = async (): Promise<void> => {
    setErrors(undefined);
    const sumRelatedQuantity = (relatedProjectIssuanceTransactions ?? []).reduce(
      (acc: number, curr) => acc + (curr.quantity ?? 0),
      0
    );
    const quantity = (parentIssuanceTransaction.quantity ?? 0) - sumRelatedQuantity;
    const totalValue = quantity * (issuanceTransactionPanelData.price ?? 0) - (issuanceTransactionPanelData.fees ?? 0);
    const res = await createIssuanceTransaction({
      issuanceTransactions: [
        {
          areaHa: issuanceTransactionPanelData.areaHa ? 0 : null,
          broker: issuanceTransactionPanelData.broker,
          counterParty: issuanceTransactionPanelData.counterParty ?? "",
          externalReference: issuanceTransactionPanelData.externalReference,
          fees: issuanceTransactionPanelData.fees,
          issuanceUuid: issuanceTransactionPanelData.issuanceUuid ?? "",
          parentTransactionUuid: issuanceTransactionPanelData.parentTransactionUuid,
          price: issuanceTransactionPanelData.price,
          quantity,
          totalValue,
          transactionDate: issuanceTransactionPanelData.transactionDate,
          type: ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_CANCELLED,
        },
      ],
    });

    if (res.status === Status.Success) {
      Toast.success({ message: "Reserved units released to inventory successfully" });
      handleCancel();
    }

    if (res.status === Status.Error) {
      setErrors([
        {
          code: "ERROR_UNABLE_TO_RELEASE_TO_INVENTORY",
          message:
            "Unable to release reserved units to inventory, please try this manually by creating a new linked transaction",
        },
      ]);
    }
  };

  const fetchRelatedChildTransactions = async (): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.parentTransactionUuid = {
      operator: "eq",
      value: parentIssuanceTransaction.uuid,
    };

    const res = await searchIssuanceTransactions({
      paging: {
        limit: 50,
        beforeCursor: null,
        afterCursor: null,
      },
      filter: { results: filterCriteria },
    });

    setRelatedProjectIssuanceTransactions(res.data?.results);
  };

  useEffect(() => {
    (async (): Promise<void> => {
      await fetchRelatedChildTransactions();
    })();
  }, [parentIssuanceTransaction.uuid]);

  return {
    handleReleaseToInventory,
    handleCancel,
    errors,
  };
};
